import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuthUser, useAuthUserAccounts, useTheme, useLang } from '@nbx/frontend-helpers/subscribe';
import { publishAuthStep, publishLang } from '@nbx/frontend-helpers/publish';
import { NBXStorage } from '@nbx/frontend-helpers/storage';
import { Context } from 'state/store';
import { setOverlay, setMenuRef, setMenuToggleRef } from 'state/creators';
import { isNullish } from 'helpers/objects';
import OVERLAYS from 'constants/overlays';
import { useCanAddFunds } from 'helpers/useCanAddFunds';
import HeaderMenuAccountTakeover from '../HeaderMenuAccountTakeover';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;

function HeaderMenuDesktop(props) {
  const { t } = useTranslation();
  const [state, dispatch] = React.useContext(Context);
  const user = useAuthUser();
  const userAccounts = useAuthUserAccounts();
  const theme = useTheme();
  const activeLang = useLang();
  const LANGS = window.LANGUAGES;
  const [savedLang, setSavedLang] = React.useState(null);
  const [startingLang, setStartingLang] = React.useState(LANGS.en);
  const menuRef = React.useRef(null);
  const toggleRef = React.useRef(null);

  const canAddFunds = useCanAddFunds();

  // set Refs
  useEffect(() => {
    dispatch(setMenuRef(menuRef.current));
    dispatch(setMenuToggleRef(toggleRef.current));
    return () => {
      dispatch(setMenuRef(null));
      dispatch(setMenuToggleRef(null));
    };
  }, [dispatch]);

  useEffect(() => {
    const getLang = async () => {
      const langFromStorage = await NBXStorage.getItem('i18nextLng', { useLocalStorage: true });
      setSavedLang(langFromStorage);
    };
    getLang();
  }, []);

  useEffect(() => {
    setStartingLang(savedLang && LANGS[savedLang] ? savedLang : LANGS.en);
  }, [savedLang, LANGS]);

  const changeActiveLang = newActiveLang => {
    publishLang(newActiveLang);
  };

  const resetAuthStep = () => {
    props.toggleMenu();
    publishAuthStep(null);
  };

  const getAccountName = userAccount => {
    if (!userAccount || !user) return null;
    let accountName = '';
    if (typeof userAccount.name === 'string') accountName = userAccount.name;
    else if (typeof user.firstName === 'string') accountName = user.firstName;
    else accountName = user.email;
    return accountName;
  };

  const openAddFunds = () => canAddFunds && dispatch(setOverlay(OVERLAYS.addfunds));
  const openQuickBuy = () => dispatch(setOverlay(OVERLAYS.quickbuy));

  const menuOpen = state.overlay === OVERLAYS.menu;
  const currentAccount = userAccounts?.find(userAccount => state.activeAccount === userAccount.id);
  const currentAccountName = getAccountName(currentAccount);
  const accountTakenOver = isNullish(currentAccount);

  function renderLoggedInMenu() {
    return (
      <nbx-card padded={false}>
        <div className={styles.cardContents}>
          {!!user && (
            <div className={styles.cardSectionAccounts}>
              {userAccounts?.map(userAccount => {
                const accountName = getAccountName(userAccount);
                const active = state.activeAccount === userAccount.id;

                const renderContents = () => {
                  return (
                    <div
                      className={classnames([styles.accountRowContents], {
                        [styles.activeAccountRowContents]: active
                      })}>
                      <nbx-avatar name={accountName} selected={active} size="small" />
                      <div className={styles.accountInfo}>
                        <nbx-text legal>{accountName}</nbx-text>
                        <nbx-text muted legal>
                          {userAccount.name
                            ? t('header_account_type_organization')
                            : t('header_account_type_individual')}{' '}
                          {/* For some reason, this whitespace is required for this nbx-text to not break when switching languages */}
                          {/* I really don't know why, so please don't remove it */}
                        </nbx-text>
                      </div>
                    </div>
                  );
                };
                return (
                  <button
                    type="button"
                    className={styles.accountRow}
                    key={userAccount.id}
                    onClick={
                      state.switchingAccount
                        ? () => {}
                        : () => props.switchAccount(state.activeAccount, userAccount.id)
                    }>
                    {renderContents()}
                  </button>
                );
              })}
            </div>
          )}
          {state.userIsAdmin && (
            <HeaderMenuAccountTakeover
              accountId={state.activeAccount}
              accountTakenOver={accountTakenOver}
              switchingAccount={state.switchingAccount}
              switchAccount={props.switchAccount}
            />
          )}
          <div className={styles.cardSectionHoriz}>
            <nbx-text>{t('header_page_mode_dark')}</nbx-text>
            <nbx-toggle toggled={theme === 'dark'} onClick={props.toggleTheme} />
          </div>
          <div className={styles.cardSectionHoriz}>
            <Link
              to={microapps?.routes?.accountPreferences?.path}
              className={styles.rowLink}
              onClick={props.toggleMenu}>
              <nbx-text>
                {t('common_language')} ({activeLang?.toUpperCase()})
              </nbx-text>
              <span className={styles.arrow}>→</span>
            </Link>
          </div>
          <div className={styles.cardSectionAuth}>
            <nbx-button primary full-width onClick={props.signOut}>
              {t('header_link_signout')}
            </nbx-button>
          </div>
        </div>
      </nbx-card>
    );
  }

  // If starting language was EN, only show EN & NO
  // else, show EN & starting language
  function renderLangButtons(languages) {
    return languages.map(key => {
      return (
        <nbx-button onClick={() => changeActiveLang(key)} inverted={key === activeLang} key={key}>
          {LANGS[key].toUpperCase()}
        </nbx-button>
      );
    });
  }

  function renderLoggedOutMenu() {
    return (
      <nbx-card padded={false}>
        <div className={styles.cardContents}>
          <div className={styles.cardSectionHoriz}>
            <nbx-text>{t('header_page_mode_dark')}</nbx-text>
            <nbx-toggle toggled={theme === 'dark'} onClick={props.toggleTheme} />
          </div>
          <div className={styles.cardSectionHoriz}>
            <nbx-text>{t('common_language')}</nbx-text>
            <div className={styles.langButtons}>
              {startingLang === 'en' && renderLangButtons(['en', 'no'])}
              {startingLang !== 'en' && renderLangButtons(['en', startingLang])}
            </div>
          </div>
          <div className={styles.cardSectionAuth}>
            <nbx-button primary full-width to={microapps?.routes?.signup?.path} onClick={resetAuthStep}>
              {t('common_sign_up')}
            </nbx-button>
            <div className={styles.spacer} />
            <nbx-button
              primary
              full-width
              inverted
              to={microapps?.routes?.login?.path}
              onClick={resetAuthStep}>
              {t('common_log_in')}
            </nbx-button>
          </div>
        </div>
      </nbx-card>
    );
  }

  return (
    <div className={styles.rightContainer}>
      {state.activeAccount && (
        <>
          <div className={styles.headerActions}>
            <nbx-button onClick={openAddFunds} icon="add_funds" disabled={!canAddFunds}>
              {t('header_addfunds_btn')}
            </nbx-button>
            <nbx-button onClick={openQuickBuy} icon="instant_trade">
              {t('header_instanttrade_btn')}
            </nbx-button>
          </div>
          <div className={styles.accountName} key={currentAccountName}>
            <nbx-text>{t('acc_account')}:</nbx-text> <nbx-text weight="bold">{currentAccountName}</nbx-text>
          </div>
        </>
      )}

      <span ref={toggleRef} className={styles.accountAvatar}>
        <nbx-avatar name={currentAccountName} onClick={props.toggleMenu} />
      </span>

      <div className={classnames([styles.menu, { [styles.menuVisible]: menuOpen }])} ref={menuRef}>
        <div className={classnames([styles.menuItems, { [styles.visible]: menuOpen }])}>
          {state.activeAccount ? renderLoggedInMenu() : renderLoggedOutMenu()}
        </div>
      </div>
    </div>
  );
}

HeaderMenuDesktop.propTypes = {
  switchAccount: PropTypes.func.isRequired,
  toggleMenu: PropTypes.func.isRequired,
  toggleTheme: PropTypes.func.isRequired,
  signOut: PropTypes.func.isRequired
};

export default HeaderMenuDesktop;
